import React from 'react';
import {
  Activity,
  Circle,
  CircleDot,
  Disc,
  Dumbbell,
  Feather,
  Flame,
  Heart,
  Swords,
  Target,
  Trophy,
  Waves,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { resolveSportIcon } from '@/modules/business/core/sportCatalog';
import { getSportSvgIcon } from '@/ui/icons/sports';
import { ThinSportIcon, normalizeThinSportIconKey } from '@/ui/icons/sports/thin';
import { cn } from '@/lib/utils';

interface SportIconProps {
  /** Explicit icon name override (stored in DB) */
  iconName?: string | null;
  /** Sport name for catalog lookup */
  sportName: string;
  /** Icon size in pixels */
  size?: number;
  /** Additional class names */
  className?: string;
}

const SPORT_ICONS: Record<string, LucideIcon> = {
  Activity,
  Circle,
  CircleDot,
  Disc,
  Dumbbell,
  Feather,
  Flame,
  Heart,
  Swords,
  Target,
  Trophy,
  Waves,
  Zap,
};

/**
 * Renders a sport icon.
 * Resolution: thin SVG icon -> catalog default -> legacy sport SVG -> Lucide fallback.
 */
export const SportIcon: React.FC<SportIconProps> = ({
  iconName,
  sportName,
  size = 16,
  className,
}) => {
  const thinIconKey = normalizeThinSportIconKey(iconName);

  if (thinIconKey) {
    return (
      <ThinSportIcon
        iconName={thinIconKey}
        size={size}
        className={className}
      />
    );
  }

  const resolvedIconName = resolveSportIcon(iconName, sportName);
  const resolvedThinIconKey = normalizeThinSportIconKey(resolvedIconName);

  if (resolvedThinIconKey) {
    return (
      <ThinSportIcon
        iconName={resolvedThinIconKey}
        size={size}
        className={className}
      />
    );
  }

  const sportSvgIcon = getSportSvgIcon(sportName);

  if (sportSvgIcon) {
    const SportSvgIcon = sportSvgIcon;
    return (
      <SportSvgIcon size={size} className={className} />
    );
  }

  const IconComponent = SPORT_ICONS[resolvedIconName] ?? Dumbbell;

  return <IconComponent size={size} className={cn('shrink-0', className)} />;
};

